import { Controller } from "@hotwired/stimulus";

// 3-step wizard for "Rattacher une entreprise" on the contact cockpit:
//   0. Type      — pick existing OR create new (radio mode cards)
//   1. Détails   — either a Select2 picker (existing) or a creation form
//                  with secteur/type/activité/pays dropdowns and a debounced
//                  duplicate check that pings /entreprises/verify
//   2. Rôle      — poste, dates, principal flag
//
// Submit is a regular form POST to /contacts/:id/entreprise_links — the
// controller reads either `contact_entreprise[entreprise_id]` (existing) or
// `contact_entreprise[new_entreprise][...]` (creation in one shot).
export default class extends Controller {
  static targets = [
    "step", "progressStep", "progressCurrent",
    "modeCard", "existingSelect",
    "nomField", "dup", "dupTitle", "dupList",
    "summary", "summaryName",
    "form", "nextBtn", "prevBtn", "submitBtn", "error",
  ];

  static values = {
    current:    { type: Number, default: 0 },
    mode:       { type: String, default: "existing" },
    juniorId:   Number,
    verifyUrl:  String,
  };

  connect() {
    this._render();
    this._dupAbort = null;
    this._dupTimer = null;
    // Track entreprise selection via Select2 events too — the searchable-select
    // is initialised by cockpit_modal_controller after the modal opens, so we
    // listen to "select2:select" on the underlying <select>.
    if (this.hasExistingSelectTarget) {
      this.existingSelectTarget.addEventListener("change", () => this._refreshSummary());
    }
  }

  disconnect() {
    if (this._dupAbort) this._dupAbort.abort();
    if (this._dupTimer) clearTimeout(this._dupTimer);
  }

  // ── Navigation ───────────────────────────────────────────────────────

  next(event) {
    event.preventDefault();
    if (!this._validateCurrent()) return;
    if (this.currentValue >= 2) return;
    this.currentValue += 1;
    this._render();
    if (this.currentValue === 2) this._refreshSummary();
  }

  prev(event) {
    event.preventDefault();
    if (this.currentValue <= 0) return;
    this.currentValue -= 1;
    this._render();
  }

  switchMode(event) {
    const mode = event.currentTarget.value;
    this.modeValue = mode;
    this.modeCardTargets.forEach((card) => {
      card.classList.toggle("csm_mode-card--active", card.dataset.mode === mode);
    });
    // Show / hide step 1 panes by mode
    this.stepTargets.forEach((s) => {
      if (Number(s.dataset.step) !== 1) return;
      s.hidden = s.dataset.mode !== mode;
      s.classList.toggle("esm_wizard__step--active",
        this.currentValue === 1 && s.dataset.mode === mode);
    });
  }

  // ── Duplicate check (creation mode) ──────────────────────────────────

  checkDuplicates(event) {
    const q = event.currentTarget.value.trim();
    if (this._dupTimer) clearTimeout(this._dupTimer);
    if (q.length < 3) {
      this._hideDup();
      return;
    }
    this._dupTimer = setTimeout(() => this._fetchDuplicates(q), 350);
  }

  _fetchDuplicates(q) {
    if (this._dupAbort) this._dupAbort.abort();
    this._dupAbort = new AbortController();
    const url = `${this.verifyUrlValue}?q=${encodeURIComponent(q)}`;
    fetch(url, { headers: { Accept: "application/json" }, signal: this._dupAbort.signal })
      .then((r) => (r.ok ? r.json() : []))
      .then((matches) => this._renderDup(matches))
      .catch(() => {});
  }

  _renderDup(matches) {
    if (!Array.isArray(matches) || matches.length === 0) {
      this._hideDup();
      return;
    }
    this.dupTitleTarget.textContent = matches.length === 1
      ? "Une entreprise au nom proche existe déjà :"
      : `${matches.length} entreprises au nom proche existent déjà :`;
    this.dupListTarget.innerHTML = "";
    matches.forEach((m) => {
      const li = document.createElement("li");
      li.className = "csm_dup__item";
      const meta = [m.type_entreprise, m.secteur, m.ville].filter(Boolean).join(" · ");
      li.innerHTML = `
        <span class="csm_dup__name">${this._esc(m.nom)}</span>
        ${meta ? `<span class="csm_dup__meta">${this._esc(meta)}</span>` : ""}
        <button type="button" class="csm_dup__use" data-id="${m.id}" data-nom="${this._esc(m.nom)}">
          <i class="fa-solid fa-arrow-right"></i> Utiliser celle-ci
        </button>
      `;
      li.querySelector(".csm_dup__use").addEventListener("click", () => {
        this._switchToExisting(m.id, m.nom);
      });
      this.dupListTarget.appendChild(li);
    });
    this.dupTarget.hidden = false;
  }

  _hideDup() {
    if (this.hasDupTarget) {
      this.dupTarget.hidden = true;
      this.dupListTarget.innerHTML = "";
    }
  }

  _switchToExisting(id, nom) {
    // Flip back to "existing" mode and pre-pick the entreprise the user
    // confirmed is the right one.
    const radio = this.modeCardTargets.find((c) => c.dataset.mode === "existing")
                    ?.querySelector("input[type=radio]");
    if (radio) {
      radio.checked = true;
      radio.dispatchEvent(new Event("change", { bubbles: true }));
    }
    if (this.hasExistingSelectTarget) {
      const $ = window.jQuery || window.$;
      // Inject the option if Select2 doesn't have it yet (it should — but be safe)
      const sel = this.existingSelectTarget;
      if (!Array.from(sel.options).some((o) => String(o.value) === String(id))) {
        const opt = new Option(nom, id, true, true);
        sel.appendChild(opt);
      }
      sel.value = id;
      if ($ && $.fn.select2) $(sel).trigger("change");
      else sel.dispatchEvent(new Event("change", { bubbles: true }));
    }
    this._hideDup();
  }

  // ── Validation ───────────────────────────────────────────────────────

  _validateCurrent() {
    this._hideError();
    if (this.currentValue === 0) {
      // Step 0 is always valid (mode is preset)
      return true;
    }
    if (this.currentValue === 1) {
      if (this.modeValue === "existing") {
        if (!this.existingSelectTarget.value) {
          this._showError("Choisis une entreprise dans la liste.");
          return false;
        }
        return true;
      }
      // Mode = new
      if (!this.nomFieldTarget.value.trim()) {
        this._showError("Le nom de l'entreprise est requis.");
        this.nomFieldTarget.focus();
        return false;
      }
      return true;
    }
    return true;
  }

  // ── Rendering ────────────────────────────────────────────────────────

  _render() {
    this.stepTargets.forEach((s) => {
      const stepIdx = Number(s.dataset.step);
      const isCurrent = stepIdx === this.currentValue;
      const isMatchingMode = !s.dataset.mode || s.dataset.mode === this.modeValue;
      const active = isCurrent && isMatchingMode;
      s.classList.toggle("esm_wizard__step--active", active);
      // Step 1 has two siblings (existing/new): only show the matching one
      if (stepIdx === 1) {
        s.hidden = !isCurrent || !isMatchingMode;
      } else {
        s.hidden = !isCurrent;
      }
    });

    this.progressStepTargets.forEach((p) => {
      const i = Number(p.dataset.step);
      p.classList.toggle("esm_wizard__progress-step--active", i === this.currentValue);
      p.classList.toggle("esm_wizard__progress-step--done", i < this.currentValue);
    });
    this.progressCurrentTarget.textContent = this.currentValue + 1;

    this.prevBtnTarget.hidden = this.currentValue === 0;
    const isLast = this.currentValue === 2;
    this.nextBtnTarget.hidden = isLast;
    this.submitBtnTarget.hidden = !isLast;
  }

  _refreshSummary() {
    if (!this.hasSummaryTarget) return;
    let label = "—";
    if (this.modeValue === "existing" && this.hasExistingSelectTarget) {
      const opt = this.existingSelectTarget.selectedOptions[0];
      if (opt) label = opt.text;
    } else if (this.modeValue === "new" && this.hasNomFieldTarget) {
      label = this.nomFieldTarget.value.trim() || "Nouvelle entreprise";
    }
    this.summaryNameTarget.textContent = label;
    this.summaryTarget.hidden = !label || label === "—";
  }

  // ── Helpers ──────────────────────────────────────────────────────────

  _showError(msg) {
    this.errorTarget.textContent = msg;
    this.errorTarget.hidden = false;
  }

  _hideError() {
    this.errorTarget.hidden = true;
    this.errorTarget.textContent = "";
  }

  _esc(s) {
    return String(s ?? "")
      .replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;").replaceAll("'", "&#39;");
  }
}
